import React, { useState, useEffect } from "react";
import {
    Bell,
    Mail,
    Check,
    Sparkles,
    Tag,
    Wrench,
    ArrowRight,
    Loader2,
    Smartphone,
    Settings,
    AlertCircle,
} from "lucide-react";
import { subscribeNotifications } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { useOneSignal } from "../hooks/useOneSignal";

export const NotificationCTA = () => {
    return (
        <section
            id="notifiche"
            data-testid="notifications-section"
            className="relative bg-nsp-yellow overflow-hidden py-24 lg:py-32"
        >
            {/* Decorative blobs */}
            <div
                aria-hidden="true"
                className="absolute -top-20 -right-20 w-96 h-96 bg-nsp-green/10 rounded-full blur-3xl"
            />
            <div
                aria-hidden="true"
                className="absolute -bottom-20 -left-20 w-96 h-96 bg-nsp-green/10 rounded-full blur-3xl"
            />

            <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 relative">
                {/* ===== HEADER ===== */}
                <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-20">
                    <div className="inline-flex items-center gap-2 bg-nsp-green text-nsp-yellow px-4 py-2 mb-6 text-xs font-bold uppercase tracking-[0.3em]">
                        <Bell className="w-4 h-4 animate-pulse" /> Resta aggiornato
                    </div>
                    <h2
                        data-testid="notifications-title"
                        className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95] text-nsp-green tracking-tighter"
                    >
                        Non perderti
                        <br />
                        <span className="bg-nsp-green text-nsp-yellow px-3 inline-block">
                            offerte e novità.
                        </span>
                    </h2>
                    <p className="text-nsp-green/80 text-base lg:text-lg mt-6 max-w-2xl mx-auto leading-relaxed">
                        Scegli come vuoi ricevere i nostri aggiornamenti: notifiche
                        istantanee sul browser, o newsletter via email. <b>O entrambe!</b>
                    </p>
                </div>

                {/* ===== TWO OPTIONS ===== */}
                <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
                    <PushOption />
                    <NewsletterOption />
                </div>

                {/* ===== BENEFITS ROW ===== */}
                <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
                    <Perk icon={Tag} title="Sconti esclusivi" text="Promo riservate agli iscritti" />
                    <Perk icon={Sparkles} title="Nuovi mezzi" text="In anteprima sulla flotta" />
                    <Perk icon={Wrench} title="Disponibilità" text="Aggiornamenti in tempo reale" />
                </div>
            </div>
        </section>
    );
};

/* =========================================================
 * OPTION A — Push notifications via OneSignal Web SDK v16
 * - App ID OneSignal: f027ebfc-cfd0-4ed5-8f8a-c09cd628efd9 (in public/index.html)
 * - I toggle "Offerte" e "Servizi" sono ATTIVI di default e vengono mappati su
 *   tag OneSignal per la segmentazione delle campagne.
 * - Le preferenze sono salvate in localStorage per UX persistente.
 * ========================================================= */
const PREFS_KEY = "nsp_push_prefs_v1";

const loadPrefs = () => {
    try {
        const raw = localStorage.getItem(PREFS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            return {
                offerte: parsed.offerte !== false,
                servizi: parsed.servizi !== false,
            };
        }
    } catch (_) {
        /* ignore */
    }
    return { offerte: true, servizi: true };
};

const savePrefs = (prefs) => {
    try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    } catch (_) {
        /* ignore */
    }
};

const PushOption = () => {
    const [prefs, setPrefs] = useState({ offerte: true, servizi: true });
    const [loading, setLoading] = useState(false);
    const { state: osState, subscribe, unsubscribe, setTags } = useOneSignal();

    // Carica preferenze da localStorage al mount
    useEffect(() => {
        setPrefs(loadPrefs());
    }, []);

    // Salva ogni cambio e sincronizza i tag con OneSignal se l'utente è iscritto
    useEffect(() => {
        savePrefs(prefs);
        if (osState.isSubscribed) {
            setTags({ offerte: prefs.offerte, servizi: prefs.servizi }).catch(() => {
                /* fallisce silenziosamente se SDK non pronto */
            });
        }
    }, [prefs, osState.isSubscribed, setTags]);

    const togglePref = (key) => {
        setPrefs((p) => {
            const next = { ...p, [key]: !p[key] };
            if (!next.offerte && !next.servizi) {
                toast.info(
                    "Hai disattivato entrambe le categorie. Riattivane almeno una per ricevere le notifiche."
                );
            }
            return next;
        });
    };

    const handleActivate = async () => {
        if (!prefs.offerte && !prefs.servizi) {
            toast.error("Attiva almeno una categoria di notifiche prima di confermare.");
            return;
        }
        if (!osState.isSupported) {
            if (osState.isIosLike) {
                toast.error(
                    "Su iOS le notifiche push richiedono iOS 16.4+ e di aggiungere il sito alla schermata Home."
                );
            } else {
                toast.error("Il tuo browser non supporta le notifiche push.");
            }
            return;
        }
        setLoading(true);
        try {
            await subscribe();
            // Imposta i tag iniziali subito dopo la sottoscrizione
            await setTags({ offerte: prefs.offerte, servizi: prefs.servizi });
            toast.success(
                `Notifiche attivate! Riceverai aggiornamenti su ${[
                    prefs.offerte && "offerte",
                    prefs.servizi && "servizi",
                ]
                    .filter(Boolean)
                    .join(" e ")}.`
            );
        } catch (e) {
            const msg = e?.message || "";
            if (msg.includes("not supported")) {
                toast.error("Notifiche non supportate in questo browser.");
            } else if (Notification?.permission === "denied") {
                toast.error(
                    "Permesso negato dal browser. Modifica le impostazioni del sito per riprovare."
                );
            } else {
                toast.error("Impossibile attivare le notifiche. Riprova.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleDeactivate = async () => {
        setLoading(true);
        try {
            await unsubscribe();
            toast.info("Notifiche disattivate. Puoi riattivarle quando vuoi.");
        } catch (_) {
            toast.error("Impossibile disattivare le notifiche.");
        } finally {
            setLoading(false);
        }
    };

    const anySelected = prefs.offerte || prefs.servizi;
    const permissionDenied = osState.permission === "denied";

    return (
        <div
            data-testid="push-option"
            className="bg-nsp-green text-white p-6 sm:p-8 lg:p-10 rounded-3xl relative overflow-hidden group"
        >
            <div
                aria-hidden="true"
                className="absolute -top-6 -right-6 opacity-5 group-hover:opacity-10 transition-opacity"
            >
                <Smartphone className="w-48 h-48" strokeWidth={1.2} />
            </div>

            <div className="relative">
                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-nsp-yellow flex items-center justify-center">
                        <Bell className="w-6 h-6 text-nsp-green" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-yellow">
                            Opzione 1 · Istantanea
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight">
                            Notifiche Push
                        </h3>
                    </div>
                </div>

                <p className="text-white/80 text-sm leading-relaxed mb-6">
                    <b className="text-nsp-yellow">Un click. Nessuna email richiesta.</b>{" "}
                    Ricevi avvisi diretti sul browser. Personalizza qui sotto cosa vuoi
                    ricevere.
                </p>

                {/* PREFERENCES TOGGLES */}
                <div
                    data-testid="push-preferences"
                    className="bg-white/5 border border-white/10 backdrop-blur-sm divide-y divide-white/10 mb-6 rounded-lg"
                >
                    <div className="flex items-center justify-between px-4 py-3 gap-3">
                        <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-nsp-yellow flex items-center gap-1.5">
                            <Settings className="w-3 h-3" />
                            Categorie attive
                        </span>
                    </div>

                    {/* Switch: Offerte */}
                    <label className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-white/[0.02]">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            <Tag className="w-5 h-5 text-nsp-yellow flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <div className="font-bold text-sm uppercase tracking-wide">
                                    Offerte
                                </div>
                                <div className="text-white/60 text-xs mt-0.5">
                                    Promozioni, codici sconto, sconti riservati
                                </div>
                            </div>
                        </div>
                        <Switch
                            data-testid="pref-offerte"
                            checked={prefs.offerte}
                            onCheckedChange={() => togglePref("offerte")}
                            className="data-[state=checked]:bg-nsp-yellow data-[state=unchecked]:bg-white/20 flex-shrink-0"
                        />
                    </label>

                    {/* Switch: Servizi */}
                    <label className="flex items-center justify-between px-4 py-4 cursor-pointer hover:bg-white/[0.02]">
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                            <Wrench className="w-5 h-5 text-nsp-yellow flex-shrink-0 mt-0.5" />
                            <div className="min-w-0">
                                <div className="font-bold text-sm uppercase tracking-wide">
                                    Servizi
                                </div>
                                <div className="text-white/60 text-xs mt-0.5">
                                    Nuovi mezzi, disponibilità urgenti, novità
                                </div>
                            </div>
                        </div>
                        <Switch
                            data-testid="pref-servizi"
                            checked={prefs.servizi}
                            onCheckedChange={() => togglePref("servizi")}
                            className="data-[state=checked]:bg-nsp-yellow data-[state=unchecked]:bg-white/20 flex-shrink-0"
                        />
                    </label>
                </div>

                {/* Stato: iscritto, permesso negato, oppure CTA per attivare */}
                {osState.isSubscribed ? (
                    <div
                        data-testid="push-success"
                        className="bg-white/10 border border-nsp-yellow/40 p-4 flex items-center gap-3 rounded-lg"
                    >
                        <Check className="w-6 h-6 text-nsp-yellow flex-shrink-0" />
                        <div className="min-w-0">
                            <div className="font-bold text-nsp-yellow uppercase tracking-wider text-sm">
                                Notifiche attive
                            </div>
                            <div className="text-white/70 text-xs mt-1">
                                Riceverai:{" "}
                                {[prefs.offerte && "Offerte", prefs.servizi && "Servizi"]
                                    .filter(Boolean)
                                    .join(", ") || "nessuna categoria attiva"}
                            </div>
                        </div>
                        <button
                            data-testid="push-reset"
                            onClick={handleDeactivate}
                            disabled={loading}
                            className="text-xs underline text-white/60 hover:text-nsp-yellow ml-auto flex-shrink-0 disabled:opacity-50"
                        >
                            Disattiva
                        </button>
                    </div>
                ) : permissionDenied ? (
                    <div
                        data-testid="push-denied"
                        className="bg-white/5 border border-red-300/40 p-4 flex items-start gap-3 rounded-lg"
                    >
                        <AlertCircle className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-white/80 leading-relaxed">
                            <b className="text-red-200">Permesso bloccato.</b> Apri le
                            impostazioni del browser per questo sito e abilita le
                            notifiche, poi ricarica la pagina.
                        </div>
                    </div>
                ) : (
                    <button
                        data-testid="push-activate"
                        onClick={handleActivate}
                        disabled={loading || !anySelected || !osState.isSupported}
                        className="w-full h-14 rounded-full bg-nsp-yellow text-nsp-green font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md active:scale-95"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" /> Attivazione...
                            </>
                        ) : (
                            <>
                                <Bell className="w-5 h-5" strokeWidth={2.5} />
                                Conferma e Attiva
                                <ArrowRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                )}

                <div className="mt-4 text-white/50 text-[11px] leading-relaxed">
                    {!osState.isSupported && osState.isIosLike ? (
                        <>
                            <b className="text-white/70">iOS:</b> richiede iOS 16.4+ e di
                            aggiungere il sito alla schermata Home per le notifiche push.
                        </>
                    ) : !osState.isSupported ? (
                        <>
                            <b className="text-white/70">Browser non supportato:</b> usa
                            Chrome, Edge, Firefox o Safari aggiornati.
                        </>
                    ) : (
                        <>
                            Le notifiche sono{" "}
                            <b className="text-white/70">pre-attivate</b> sulle categorie
                            consigliate. Disattiva quelle che non ti interessano. Il
                            browser ti chiederà conferma al primo "Conferma e Attiva".
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

/* =========================================================
 * OPTION B — Newsletter via email
 * ========================================================= */
const NewsletterOption = () => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!/^\S+@\S+\.\S+$/.test(email)) {
            toast.error("Inserisci un indirizzo email valido");
            return;
        }
        setLoading(true);
        try {
            await subscribeNotifications(email);
            setDone(true);
            toast.success("Iscrizione completata!");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore nell'iscrizione");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="newsletter-option"
            className="bg-white text-nsp-green p-8 lg:p-10 rounded-3xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.05)] relative overflow-hidden group"
        >
            <div
                aria-hidden="true"
                className="absolute -top-6 -right-6 opacity-[0.04] group-hover:opacity-[0.08] transition-opacity"
            >
                <Mail className="w-48 h-48 text-nsp-green" strokeWidth={1.2} />
            </div>

            <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-nsp-green flex items-center justify-center">
                        <Mail className="w-6 h-6 text-nsp-yellow" strokeWidth={2.5} />
                    </div>
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-nsp-green/70">
                            Opzione 2 · Approfondita
                        </div>
                        <h3 className="font-display text-2xl uppercase tracking-tight text-nsp-green">
                            Newsletter Email
                        </h3>
                    </div>
                </div>

                <p className="text-nsp-green/80 text-sm leading-relaxed mb-3">
                    <b>La nostra newsletter mensile</b> con offerte esclusive, codici
                    sconto a tempo, guide tecniche e novità sulla flotta.
                </p>
                <ul className="text-nsp-green/70 text-xs space-y-2 mb-6">
                    <li className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 mt-0.5 text-nsp-green flex-shrink-0" />
                        Codici sconto riservati agli iscritti
                    </li>
                    <li className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 mt-0.5 text-nsp-green flex-shrink-0" />
                        Contenuti tecnici e consigli per i tuoi cantieri
                    </li>
                    <li className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 mt-0.5 text-nsp-green flex-shrink-0" />
                        Cancellabile in un click, no spam
                    </li>
                </ul>

                {done ? (
                    <div
                        data-testid="newsletter-success"
                        className="bg-nsp-green/5 border-2 border-nsp-green p-5 flex items-center gap-3"
                    >
                        <Check className="w-6 h-6 text-nsp-green flex-shrink-0" />
                        <div>
                            <div className="font-bold uppercase tracking-wider text-sm">
                                Iscrizione effettuata!
                            </div>
                            <div className="text-nsp-green/70 text-xs mt-1">
                                Riceverai la prima newsletter a breve.
                            </div>
                        </div>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="flex flex-col gap-3"
                    >
                        <input
                            data-testid="newsletter-email"
                            type="email"
                            placeholder="tua@email.it"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            maxLength={150}
                            autoComplete="email"
                            className="h-14 bg-nsp-bg text-nsp-green px-4 border-2 border-nsp-border focus:outline-none focus:border-nsp-green font-bold tracking-wide placeholder:text-zinc-400"
                        />
                        <button
                            data-testid="newsletter-submit"
                            type="submit"
                            disabled={loading}
                            className="h-14 rounded-full bg-nsp-green text-nsp-yellow font-bold uppercase tracking-widest text-sm flex items-center justify-center gap-3 hover:bg-nsp-yellow hover:text-nsp-green transition-colors disabled:opacity-50 shadow-md active:scale-95"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" /> Iscrizione...
                                </>
                            ) : (
                                <>
                                    <Mail className="w-5 h-5" strokeWidth={2.5} />
                                    Iscriviti alla Newsletter
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </form>
                )}

                <div className="mt-4 text-nsp-green/60 text-[11px] leading-relaxed">
                    Iscrivendoti accetti l'<a href="#privacy" className="underline font-bold">informativa privacy</a>. Puoi cancellarti in ogni momento.
                </div>
            </div>
        </div>
    );
};

const Perk = ({ icon: Icon, title, text }) => (
    <div className="bg-nsp-green/5 border border-nsp-green/20 p-4">
        <Icon className="w-6 h-6 text-nsp-green mb-2" strokeWidth={2} />
        <div className="font-bold text-nsp-green text-sm uppercase tracking-wide">
            {title}
        </div>
        <div className="text-nsp-green/70 text-xs mt-1">{text}</div>
    </div>
);

export default NotificationCTA;
